/**
 * Иконки интерфейса.
 *
 * Нарисованы контуром в общей сетке 24×24 и красятся `currentColor`, поэтому
 * подхватывают тему сами. Свой набор вместо библиотеки: нужно полтора десятка
 * значков, а любая библиотека тянет тысячи и весит больше всего остального.
 *
 * Отдельные значки взяты из набора Lucide (лицензия ISC, lucide.dev) — он
 * нарисован в той же сетке и тем же контуром. Такие помечены в комментарии
 * рядом со значком; копируется путь, а не зависимость.
 */

import type { ReactNode, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 18, children, ...rest }: IconProps & { children: ReactNode }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconOverview(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 13a8 8 0 0 1 16 0" />
      <path d="M12 13l4-3" />
      <path d="M4 17h16" />
    </Icon>
  );
}

export function IconEvents(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </Icon>
  );
}

export function IconPlayers(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 6.5a3 3 0 0 1 0 5.6M17.5 19a5.4 5.4 0 0 0-2-4.2" />
    </Icon>
  );
}

export function IconApplications(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M3 12h5l1.6 2.6h4.8L16 12h5" />
      <path d="M5.2 6.2 3 12v5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5l-2.2-5.8A2 2 0 0 0 16.9 5H7.1a2 2 0 0 0-1.9 1.2Z" />
    </Icon>
  );
}

export function IconRollbacks(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M3.5 9a8.5 8.5 0 1 1-.4 5" />
      <path d="M3 4.5V9h4.5" />
      <path d="M12 8v4.3l2.8 1.7" />
    </Icon>
  );
}

export function IconStats(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M22 20H2" />
    </Icon>
  );
}

export function IconBroadcast(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 9.5v5a1.5 1.5 0 0 0 1.5 1.5H8l6 4V5.5L8 9.5H5.5A1.5 1.5 0 0 0 4 11Z" />
      <path d="M17.5 9a4 4 0 0 1 0 6" />
      <path d="M20 6.5a7.5 7.5 0 0 1 0 11" />
    </Icon>
  );
}

export function IconAudit(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 6h10M4 12h16M4 18h12" />
      <circle cx="18.5" cy="6" r="1.6" />
    </Icon>
  );
}

export function IconSettings(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a2 2 0 1 1 0-4h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.3a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 2.8 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.5 1.1Z" />
    </Icon>
  );
}

export function IconSetup(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M14.5 3.5a5 5 0 0 0 6 6.6l-9 9a3.2 3.2 0 0 1-4.5-4.5l9-9Z" />
      <path d="M6.5 17.5h.01" />
    </Icon>
  );
}

/** Дефолты: сложенная карта — вкладка про разметку тактики на карте. */
export function IconTactics(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M9 4.5 3.5 6.8v12.7L9 17.2l6 2.3 5.5-2.3V4.5L15 6.8Z" />
      <path d="M9 4.5v12.7M15 6.8v12.7" />
    </Icon>
  );
}

/** Инструменты доски. Рисуются в той же сетке, что и значки меню. */
export function IconCursor(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M5.5 3.5 19 12l-6.2 1.3L9.6 19Z" />
    </Icon>
  );
}

export function IconBrush(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 20c1.8.4 3.3-.3 4-1.7.8-1.6-.4-3-1.9-2.8-1.3.2-2.1 1.6-2.1 4.5Z" />
      <path d="M8.5 15.5 18.4 5.6a2 2 0 0 1 2.8 2.8L11.3 18.3" />
    </Icon>
  );
}

export function IconArrow(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 20 20 4" />
      <path d="M20 11V4h-7" />
    </Icon>
  );
}

/** Рука: двигать нарисованное и карту. */
export function IconHand(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M9 11V5.2a1.6 1.6 0 0 1 3.2 0V11" />
      <path d="M12.2 10.6V4.4a1.6 1.6 0 0 1 3.2 0v6.2" />
      <path d="M15.4 11V6.6a1.6 1.6 0 0 1 3.2 0V14a6 6 0 0 1-6 6h-1a5 5 0 0 1-4.2-2.3L4.6 13a1.6 1.6 0 0 1 2.6-1.8L9 13.4" />
    </Icon>
  );
}

/** Пунктирная линия. */
export function IconDashed(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M3.5 17.5c2-.4 3-1.6 3.6-3.2M9.5 11.5c.8-1.3 1.8-2.6 3.2-4M15.5 5.5c.9-.8 2-1.2 3.4-1.3" strokeDasharray="3.4 2.6" />
      <path d="M4 20h16" strokeDasharray="3.4 2.6" />
    </Icon>
  );
}

/**
 * Машина с экипажем.
 *
 * Взята из набора Lucide (лицензия ISC, https://lucide.dev/icons/car): он
 * нарисован в той же сетке 24×24 контуром, что и остальные значки здесь, и
 * машина в нём узнаётся с первого взгляда — нарисованная руками не узнавалась.
 */
export function IconCar(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
      <circle cx="7" cy="17" r="2" />
      <path d="M9 17h6" />
      <circle cx="17" cy="17" r="2" />
    </Icon>
  );
}

/** Коллер: рупор — тот, кто ведёт состав голосом. */
export function IconCaller(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 9.5h3l6-3.6v12.2l-6-3.6H4Z" />
      <path d="M16.6 9a4 4 0 0 1 0 6" />
      <path d="M19.2 6.6a7.5 7.5 0 0 1 0 10.8" />
    </Icon>
  );
}

/** Прицел: инструмент «Огонь» — куда стрелять. */
export function IconTarget(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="5.5" />
      <path d="M12 2.5v5M12 16.5v5M2.5 12h5M16.5 12h5" />
    </Icon>
  );
}

/** Во весь экран и обратно. */
export function IconExpand(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M9 4.5H4.5V9M15 4.5h4.5V9M9 19.5H4.5V15M15 19.5h4.5V15" />
    </Icon>
  );
}

export function IconCollapse(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4.5 9H9V4.5M19.5 9H15V4.5M4.5 15H9v4.5M19.5 15H15v4.5" />
    </Icon>
  );
}

/**
 * Экипажи скрыты: человек с косой чертой.
 *
 * Отдельно от «скрыть разметку» — глаза: скрывают они разное, и одинаковые
 * значки путали бы.
 */
export function IconCrewOff(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M10.7 6.2a3.2 3.2 0 0 1 4.3 4.3" />
      <path d="M8.9 8.9a3.2 3.2 0 0 0 3.9 3.9" />
      <path d="M6.5 19a5.5 5.5 0 0 1 7.4-5.2" />
      <path d="M17.5 19a5.4 5.4 0 0 0-1.2-3.4" />
      <path d="m4 4 16 16" />
    </Icon>
  );
}

export function IconEye(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </Icon>
  );
}

export function IconEyeOff(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 4l16 16" />
      <path d="M9.5 6.1A9.7 9.7 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.2 3.9M6.6 8.2A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1 0 1.9-.2 2.7-.5" />
    </Icon>
  );
}

export function IconMarker(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8.5v7M8.5 12h7" />
    </Icon>
  );
}

export function IconText(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M5 6.5V5h14v1.5M12 5v14M9 19h6" />
    </Icon>
  );
}

export function IconEraser(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="m4.5 15.5 6-6a2 2 0 0 1 2.8 0l4.2 4.2a2 2 0 0 1 0 2.8l-3 3H8Z" />
      <path d="M4.5 20h15" />
    </Icon>
  );
}

export function IconUndo(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 9h10a5 5 0 0 1 0 10H9" />
      <path d="M8 5 4 9l4 4" />
    </Icon>
  );
}

export function IconRedo(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M20 9H10a5 5 0 0 0 0 10h5" />
      <path d="m16 5 4 4-4 4" />
    </Icon>
  );
}

export function IconDownload(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M12 4v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4.5 19h15" />
    </Icon>
  );
}

export function IconTrash(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4.5 6.5h15M9.5 6.5V4.5h5v2" />
      <path d="M6.5 6.5 7.5 20h9l1-13.5" />
      <path d="M10.5 10v6M13.5 10v6" />
    </Icon>
  );
}

export function IconSun(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4" />
    </Icon>
  );
}

export function IconMoon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
    </Icon>
  );
}

export function IconMenu(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  );
}

export function IconChevronDown(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="m6 9.5 6 6 6-6" />
    </Icon>
  );
}

export function IconCheck(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="m5 12.5 4.5 4.5L19 7" />
    </Icon>
  );
}

export function IconPlus(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function IconExternal(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />
    </Icon>
  );
}

/** Захват для перетаскивания: показывает, что карточку можно тянуть. */
export function IconGrip(props: IconProps): ReactNode {
  return (
    <Icon {...props} strokeWidth="2">
      <path d="M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01" />
    </Icon>
  );
}

/** Замок: экран отказа в доступе. */
export function IconLock(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
      <path d="M12 14.5v2.5" />
    </Icon>
  );
}

export function IconLogout(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M15 8V6.5A1.5 1.5 0 0 0 13.5 5h-7A1.5 1.5 0 0 0 5 6.5v11A1.5 1.5 0 0 0 6.5 19h7a1.5 1.5 0 0 0 1.5-1.5V16" />
      <path d="M10 12h10M17 9l3 3-3 3" />
    </Icon>
  );
}
